import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { api } from './api';
import { buildMemorialHtml } from './exportHtml';
import type { MediaItem, Offering } from '../hooks/useMemorialData';

interface Tribute {
  id: string;
  fromName: string;
  message: string | null;
  createdAt: number;
}
interface Theme {
  slug: string;
  name: string;
  config: Record<string, string>;
}
interface ExportArticle {
  id: string;
  title: string;
  excerpt: string | null;
  content: string;
  createdAt: number;
}

/**
 * 纪念册导出（ZIP）：在前端完成打包（JSZip），服务器只提供数据，
 * 不受 Worker CPU 限制。产物含 index.html（自包含）、影像原文件、结构化 JSON。
 */
export async function downloadMemorialZip(
  memorialId: string,
  name: string,
  onProgress?: (msg: string) => void,
): Promise<void> {
  const step = (m: string) => onProgress?.(m);

  const [mediaRes, offerRes, tribRes, artRes] = await Promise.all([
    api<{ items: MediaItem[] }>(`/media/memorial/${memorialId}`),
    api<{ items: Offering[] }>(`/offerings/memorial/${memorialId}`),
    api<{ items: Tribute[] }>(`/tributes/memorial/${memorialId}`),
    // full=1 附带正文，用于离线归档（列表默认不带 content）
    api<{ items: ExportArticle[] }>(`/articles?memorialId=${memorialId}&full=1`),
  ]);

  const memorialRes = await api<{ memorial: any }>(`/memorials/${memorialId}`);
  const memorial = memorialRes.data?.memorial;
  if (!memorial) throw new Error('纪念馆数据加载失败');

  // 主题配置（保持原馆配色）
  let theme: Theme | null = null;
  if (memorial.themeId) {
    const th = await api<{ theme: Theme }>(`/themes/${memorial.themeId}`);
    if (th.ok && th.data?.theme) theme = th.data.theme;
  }

  const media = mediaRes.data?.items ?? [];
  const articleItems = artRes.data?.items ?? [];
  const photos = media.filter((m) => m.type === 'photo');
  const videos = media.filter((m) => m.type === 'video');

  const zip = new JSZip();
  const imgFolder = zip.folder('images')!;

  for (let i = 0; i < photos.length; i++) {
    step(`正在打包照片 ${i + 1}/${photos.length}`);
    const blob = await fetch(photos[i].url).then((r) => r.blob());
    imgFolder.file(`photo-${i + 1}.jpg`, blob);
  }
  for (let i = 0; i < videos.length; i++) {
    step(`正在打包视频 ${i + 1}/${videos.length}`);
    const blob = await fetch(videos[i].url).then((r) => r.blob());
    imgFolder.file(`video-${i + 1}.mp4`, blob);
  }

  step('正在生成页面…');
  const html = buildMemorialHtml({
    memorial,
    media,
    offerings: offerRes.data?.items ?? [],
    tributes: tribRes.data?.items ?? [],
    articles: articleItems,
    // 合葬：详情接口已返回馆内逝者（含签名遗像地址）
    people: memorial.people ?? [],
    theme: theme?.config ?? null,
    notary: null,
  });

  zip.file('index.html', html);
  zip.file('memorial.json', JSON.stringify(memorial, null, 2));
  zip.file(
    'tributes.json',
    JSON.stringify(
      {
        offerings: offerRes.data?.items ?? [],
        tributes: tribRes.data?.items ?? [],
      },
      null,
      2,
    ),
  );
  zip.file('articles.json', JSON.stringify({ articles: articleItems }, null, 2));
  zip.file(
    '使用说明.txt',
    [
      '纪念馆纪念册',
      '',
      '1. 双击 index.html 即可在浏览器中打开，无需联网、无需任何服务器。',
      '2. 可自行上传到网盘、传给亲友，或刻录保存。',
      '3. memorial.json / tributes.json / articles.json 是结构化原始数据，便于将来迁移到其他平台。',
      '4. images 目录存放全部照片与视频原文件。',
      '',
      `导出时间：${new Date().toLocaleString('zh-CN')}`,
    ].join('\n'),
  );

  step('正在压缩…');
  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `${name}-纪念馆.zip`);
}
